import { useState, useRef, useEffect } from 'react';
import { formatDue, formatDateShort, startOfDay, addDays, duePreset } from '@/lib/dates.ts';

/**
 * Replaces <input type="datetime-local">, which rendered the OS calendar glyph
 * and an empty "dd/mm/yyyy --:-- --" placeholder that looked nothing like the
 * rest of the app.
 *
 * Shows the same relative wording as the task rows ("Today 17:00", "3d
 * overdue") rather than a raw date, so the table and the lists agree.
 */
export function DateField({
  value,
  onChange,
  ariaLabel,
  align = 'left',
  settled = false,
}: {
  value: string | null;
  onChange: (iso: string | null) => void;
  ariaLabel: string;
  align?: 'left' | 'right';
  /** Task is done or cancelled — show the plain date, never "overdue". */
  settled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => startOfDay(value ? new Date(value) : new Date()));
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setMonth(startOfDay(value ? new Date(value) : new Date()));

    function onPointerDown(e: PointerEvent) {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, value]);

  const selected = value ? startOfDay(new Date(value)) : null;
  const today = startOfDay(new Date());

  /** Keeps the existing time of day when only the date is picked. */
  function pickDay(day: Date) {
    const next = new Date(day);
    const existing = value ? new Date(value) : null;
    next.setHours(existing ? existing.getHours() : 9, existing ? existing.getMinutes() : 0, 0, 0);
    onChange(next.toISOString());
    setOpen(false);
  }

  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  // Monday-first, which is what a working week looks like.
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 rounded-xs border border-transparent px-1.5 py-1 text-left text-xs transition-colors duration-[120ms] ease-out hover:border-line hover:bg-hover"
      >
        <svg viewBox="0 0 14 14" aria-hidden="true" className="size-3 shrink-0 text-ink-3">
          <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" fill="none" stroke="currentColor" />
          <path d="M1.5 5.5h11M4.5 1.5v2M9.5 1.5v2" stroke="currentColor" strokeLinecap="round" />
        </svg>
        <span className={value ? 'text-ink-2' : 'text-ink-3'} data-numeric>
          {value ? (settled ? formatDateShort(value) : formatDue(value)) : 'No date'}
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={ariaLabel}
          className={`absolute z-50 mt-1 w-60 rounded-sm border border-line bg-overlay p-2 shadow-lg shadow-black/40 ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <div className="mb-2 flex flex-wrap gap-1">
            {(['today', 'tomorrow', 'next_week'] as const).map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => {
                  onChange(duePreset(preset)!.toISOString());
                  setOpen(false);
                }}
                className="rounded-full border border-line bg-inset px-2 py-0.5 text-xs text-ink-2 transition-colors duration-[120ms] ease-out hover:border-line-strong hover:text-ink"
              >
                {preset === 'next_week' ? 'Next week' : preset === 'today' ? 'Today' : 'Tomorrow'}
              </button>
            ))}
            {value && (
              <button
                type="button"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className="rounded-full px-2 py-0.5 text-xs text-brick transition-colors duration-[120ms] ease-out hover:bg-brick/10"
              >
                Clear
              </button>
            )}
          </div>

          <div className="mb-1 flex items-center justify-between">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
              className="rounded-xs px-1.5 py-0.5 text-ink-3 hover:bg-hover hover:text-ink"
            >
              ‹
            </button>
            <span className="text-xs text-ink-2">
              {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </span>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
              className="rounded-xs px-1.5 py-0.5 text-ink-3 hover:bg-hover hover:text-ink"
            >
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
              <span key={i} className="pb-1 text-center text-[10px] text-ink-3">
                {d}
              </span>
            ))}
            {Array.from({ length: lead }, (_, i) => <span key={`lead-${i}`} />)}
            {Array.from({ length: days }, (_, i) => {
              const day = new Date(month.getFullYear(), month.getMonth(), i + 1);
              const isSelected = selected?.getTime() === day.getTime();
              const isToday = today.getTime() === day.getTime();
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => pickDay(day)}
                  aria-current={isToday ? 'date' : undefined}
                  className={`aspect-square rounded-xs text-xs transition-colors duration-[120ms] ease-out ${
                    isSelected
                      ? 'bg-brass text-page'
                      : isToday
                        ? 'text-brass hover:bg-hover'
                        : 'text-ink-2 hover:bg-hover hover:text-ink'
                  }`}
                  data-numeric
                >
                  {i + 1}
                </button>
              );
            })}
          </div>

          <label className="mt-2 flex items-center gap-2 border-t border-line pt-2">
            <span className="t-meta shrink-0 text-ink-3">Time</span>
            <input
              type="time"
              value={
                value
                  ? `${String(new Date(value).getHours()).padStart(2, '0')}:${String(new Date(value).getMinutes()).padStart(2, '0')}`
                  : '09:00'
              }
              onChange={(e) => {
                const [h, m] = e.target.value.split(':').map(Number);
                const base = value ? new Date(value) : addDays(startOfDay(new Date()), 0);
                base.setHours(h ?? 9, m ?? 0, 0, 0);
                onChange(base.toISOString());
              }}
              className="w-full rounded-xs border border-line bg-inset px-1.5 py-0.5 text-xs text-ink focus:border-brass focus:outline-none"
              data-numeric
            />
          </label>
        </div>
      )}
    </div>
  );
}
